import styles from './Sidebar.scss';

import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import debounce from 'lodash/debounce';
import React from 'react';

import { Navigation } from 'src/api/contentful/contentTypes.cloverdotcom';
import { getField, isEntry } from 'src/utils/EntryUtil';

import { Collapsible } from '../Collapsible/Collapsible';
import { withBrowser } from '../Responsive/WithBrowser';
import { withSidebarContext } from '../SidebarContext/WithSidebarContext';
import { ClosedMenu } from './ClosedMenu/ClosedMenu';
import { OpenMenu } from './OpenMenu/OpenMenu';

interface ISidebar extends React.AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<Navigation>;
  forceClose?: boolean;
  mobile: boolean;
  backgroundColor?: string;
  open: boolean;
  ignoreWindowEvents: boolean;
  closeSidebar: () => void;
  children?: never;
}

type UiState = {
  isOpen: boolean;
};

class Sidebar extends React.PureComponent<ISidebar, UiState> {

  readonly el = React.createRef<HTMLElement>();

  readonly onWindowScroll = debounce(() => {
    const { mobile, ignoreWindowEvents, closeSidebar } = this.props;
    if (!mobile && ignoreWindowEvents) return;
    closeSidebar();
  }, 100, { maxWait: 200 });

  readonly onWindowClick = debounce((e: MouseEvent) => {
    const { mobile, ignoreWindowEvents, closeSidebar } = this.props;
    const { target } = e;
    const { current } = this.el;

    // did the click come from the sidebar or sidebar contents
    if ((!mobile && ignoreWindowEvents) || target instanceof Node && current && (current === target || current.contains(target))) return;
    closeSidebar();
  }, 100, { maxWait: 200, leading: true }); // leading makes sure this happens on the first function call so that the target is in the DOM

  readonly state: UiState = { isOpen: false };

  static getDerivedStateFromProps(nextProps: Readonly<ISidebar>, prevState: UiState): UiState {
    return {
      ...prevState,
      isOpen: nextProps.forceClose ? false : nextProps.open,
    };
  }

  componentDidMount() {
    window.addEventListener('click', this.onWindowClick);
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.onWindowClick);
    window.removeEventListener('scroll', this.onWindowScroll);
  }

  render() {
    const { isOpen } = this.state;

    const {
      entry,
      forceClose,
      mobile,
      open,
      backgroundColor,
      closeSidebar,
      ignoreWindowEvents,
      className,
      ...props
    } = this.props;

    if (!isEntry(entry)) return null;

    const closedMenu = (
      <ClosedMenu
        hidden={isOpen}
        title={getField(entry, 'title')}
        linkWhenNotOpenable={getField(entry, 'primaryLink')}
      />
    );
    const openMenu = <OpenMenu entry={entry} hidden={!isOpen} />;

    return (
      <div className={styles.sidebarAnchor}>
        <div
          className={cx(styles.sidebarWrapper, {
           [styles.sidebarWrapperOpen]: isOpen,
          })}
        >
          <aside
            ref={this.el}
            data-test={open ? 'side-menu-open' : 'side-menu-closed'}
            className={cx(styles.sidebar, className, {
              [styles.sidebarInNavbar]: !mobile,
              [styles.sidebarMobile]: mobile,
              [styles.sidebarOpen]: isOpen,
              [styles.sidebarForceClose]: !isOpen && forceClose,
            })}
            style={{
              backgroundColor: backgroundColor || getField(entry, 'backgroundColor'),
            }}
            {...props}
          >
            {mobile ? <Collapsible collapsed={isOpen}>{closedMenu}</Collapsible> : closedMenu}
            {mobile ? <Collapsible collapsed={!isOpen}>{openMenu}</Collapsible> : openMenu}
          </aside>
        </div>
    </div>
    );
  }
}

const SidebarContexted = withSidebarContext(
  ({ isOpen, ignoreWindowEvents, backgroundColor, close }) => ({
    open: isOpen,
    ignoreWindowEvents,
    backgroundColor,
    closeSidebar: close,
  })
)(Sidebar);

const Browsered = withBrowser(browser => ({ mobile: browser.is.mobile }))(SidebarContexted);

export {
  Browsered as Sidebar,
  Sidebar as _Sidebar,
};
